import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { CircleHelp, FileText, ShieldCheck, X } from "lucide-react";
import { toSabahMalay, type InterfaceLanguage } from "./i18n";

export type RegistrationInfoTopic = "help" | "privacy" | "terms";
type Language = InterfaceLanguage;

const content = {
  en: {
    close: "Close",
    help: {
      title: "Kitanego Help",
      intro: "Create your account and add your needs in a few simple steps.",
      sections: [
        ["Register", "Continue with your Google account, or enter your name and email manually."],
        ["Choose your area", "Select your Sabah district so Kitanego can group local demand."],
        ["Add your needs", "Choose the categories and products you regularly need."],
        ["Manage your details", "After registering, you can review and update your profile from User Details."],
      ],
    },
    privacy: {
      title: "Kitanego Privacy",
      intro: "Your personal details stay private while your product needs contribute to grouped demand.",
      sections: [
        ["Information we use", "Your name, email, district, selected categories and products, and registration location when required for Sabah coverage."],
        ["How demand is shared", "Suppliers receive grouped product-demand totals. Your name, email and precise device location are not included in supplier reports."],
        ["Your control", "You can review and update your profile details from User Details."],
        ["No financial information", "Kitanego does not ask for banking details or payment during registration."],
      ],
    },
    terms: {
      title: "Kitanego Terms",
      intro: "By creating an account, you agree to use Kitanego lawfully and provide accurate information.",
      sections: [
        ["Demand signals", "Your selections express product needs. They are not purchases, orders or payment commitments."],
        ["Opportunities", "Kitanego may use grouped demand to approach suppliers, but a discount, offer or product availability is not guaranteed."],
        ["Account responsibility", "Keep your account secure and update information that is no longer accurate."],
        ["Acceptable use", "Do not misuse the service, submit false information or interfere with other members."],
      ],
    },
  },
  ms: {
    close: "Tutup",
    help: {
      title: "Bantuan Kitanego",
      intro: "Cipta akaun anda dan tambah keperluan anda dalam beberapa langkah mudah.",
      sections: [
        ["Daftar", "Teruskan dengan akaun Google anda, atau masukkan nama dan e-mel secara manual."],
        ["Pilih kawasan", "Pilih daerah anda di Sabah supaya Kitanego boleh mengumpulkan permintaan tempatan."],
        ["Tambah keperluan", "Pilih kategori dan produk yang anda perlukan secara tetap."],
        ["Urus butiran anda", "Selepas mendaftar, anda boleh menyemak dan mengemas kini profil melalui Butiran Pengguna."],
      ],
    },
    privacy: {
      title: "Privasi Kitanego",
      intro: "Butiran peribadi anda kekal sulit sementara keperluan produk anda menyumbang kepada permintaan berkumpulan.",
      sections: [
        ["Maklumat yang digunakan", "Nama, e-mel, daerah, kategori dan produk pilihan anda, serta lokasi pendaftaran apabila diperlukan untuk liputan Sabah."],
        ["Cara permintaan dikongsi", "Pembekal menerima jumlah permintaan produk berkumpulan. Nama, e-mel dan lokasi tepat peranti anda tidak disertakan dalam laporan pembekal."],
        ["Kawalan anda", "Anda boleh menyemak dan mengemas kini butiran profil melalui Butiran Pengguna."],
        ["Tiada maklumat kewangan", "Kitanego tidak meminta butiran bank atau bayaran semasa pendaftaran."],
      ],
    },
    terms: {
      title: "Terma Kitanego",
      intro: "Dengan mencipta akaun, anda bersetuju menggunakan Kitanego secara sah dan memberikan maklumat yang tepat.",
      sections: [
        ["Isyarat permintaan", "Pilihan anda menyatakan keperluan produk. Ia bukan pembelian, pesanan atau komitmen bayaran."],
        ["Peluang", "Kitanego boleh menggunakan permintaan berkumpulan untuk menghubungi pembekal, tetapi diskaun, tawaran atau ketersediaan produk tidak dijamin."],
        ["Tanggungjawab akaun", "Pastikan akaun anda selamat dan kemas kini maklumat yang tidak lagi tepat."],
        ["Penggunaan yang dibenarkan", "Jangan menyalahgunakan perkhidmatan, menghantar maklumat palsu atau mengganggu ahli lain."],
      ],
    },
  },
} as const;

const icons = { help: CircleHelp, privacy: ShieldCheck, terms: FileText };

export default function RegistrationInfoDialog({ topic, language, onClose }: { topic: RegistrationInfoTopic; language: Language; onClose: () => void }) {
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  const languageCopy = content[language === "en" ? "en" : "ms"];
  const baseCopy = languageCopy[topic];
  const localise = language === "sabah" ? toSabahMalay : (value: string) => value;
  const copy = {
    title: localise(baseCopy.title),
    intro: localise(baseCopy.intro),
    sections: baseCopy.sections.map(([heading, detail]) => [localise(heading), localise(detail)] as const),
  };
  const closeLabel = localise(languageCopy.close);
  const Icon = icons[topic];

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  return createPortal(
    <div className="registration-info-dialog" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="registration-info-dialog__window" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="registration-info-dialog__header">
          <span className={`registration-info-dialog__icon registration-info-dialog__icon--${topic}`} aria-hidden="true"><Icon /></span>
          <div><h2 id={titleId}>{copy.title}</h2></div>
          <button ref={closeButton} type="button" className="registration-info-dialog__close" onClick={onClose} aria-label={closeLabel}><X /></button>
        </header>
        <p className="registration-info-dialog__intro">{copy.intro}</p>
        <div className="registration-info-dialog__sections">
          {copy.sections.map(([heading, detail]) => <section key={heading}><h3>{heading}</h3><p>{detail}</p></section>)}
        </div>
        <button type="button" className="registration-info-dialog__done" onClick={onClose}>{closeLabel}</button>
      </section>
    </div>,
    document.body,
  );
}
